import * as React from "react";
import { isEqual, compact, map } from "lodash";
import { FixedCommentSetHamburgerMenu } from "./HamburgerMenu";
import { DefaultCommentMutationModel } from "../../../misc/api/FixedComments/apiTypes";
import { DefaultFixedCommentOptionTypes, DefaultAcceptableCommentDropdown } from "./Dropdowns";
import { Form } from "informedv4";
import { FixedCommentSetTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetTypes";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import { FixedCommentSet, WebPortalClient } from "webportalclient";
import { Column, Table } from "../../../Components/QueryTable";
import { useQuery } from "../../../Components/Query";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { useWebPortalApiClient } from "../../../misc/api/WebPortalTokenClient";

interface FixedCommentsTableProps {

}

export interface CommentEntry {
    DefaultComment: string;
    SetType: FixedCommentSetTypeEnum;
}

interface DefaultCommentEntry {
    [key: string]: CommentEntry;
}

export interface DefaultCommentFormModel {
    defaultComments: DefaultCommentEntry;
}

function ExtractDefaultCommentsFromSummary(sets: FixedCommentSet[]): DefaultCommentFormModel {

    const state: DefaultCommentFormModel = {
        defaultComments: {

        }
    };

    sets.forEach(set => {
        state.defaultComments[set.id] = {
            DefaultComment: set.defaultComment ? set.defaultComment.id : DefaultFixedCommentOptionTypes.None,
            SetType: set.studySet.commentSetType as FixedCommentSetTypeEnum
        }
    })

    return state;
}

function GetChangedValues(values: DefaultCommentFormModel, initialValues: DefaultCommentFormModel): DefaultCommentMutationModel[] {

    const changedValues = map(values.defaultComments, (v, k) => {

        let defaultComment = initialValues.defaultComments[k];

        return !isEqual(v, defaultComment) ? {
            defaultCommentId: v.DefaultComment !== DefaultFixedCommentOptionTypes.None ? v.DefaultComment : null,
            setId: k,
        } as DefaultCommentMutationModel : null;

    });

    return compact(changedValues);
}

const FixedCommentsTable = ({ }: FixedCommentsTableProps): JSX.Element => {

    const [initialDefaultComments, setInitialDefaultComments] = React.useState<DefaultCommentFormModel>();
    const [isBusy, setIsBusy] = React.useState<boolean>(false);
    const [err, setErr] = React.useState<string>();
    const [success, setSuccess] = React.useState<boolean>(false);
    const [showAuditModal, setShowAuditModal] = React.useState<boolean>(false);
    const [changedDefaultComments, setChangedDefaultComments] = React.useState<DefaultCommentMutationModel[]>([]);
    const client = useWebPortalApiClient();


    const { data } = useQuery();
    const { sessionTypes, fixedCommentSetTypes, fixedCommentSetEntityTypes } = useReferenceContextProvider();

    React.useEffect(() => {

        const fetchData = async () => {
            if (data.length > 0) {
                setInitialDefaultComments(ExtractDefaultCommentsFromSummary(data));
            }
        }

        if (!showAuditModal) {
            setInitialDefaultComments(null);
            fetchData();
        }

    }, [showAuditModal, data]);

    const handleSubmit = () => {
        setSuccess(false);
        setShowAuditModal(true);
    };

    const handleAuditSubmit = async (auditForm: AuditForm) => {

        setIsBusy(true);

        try {
            for (const set of changedDefaultComments) {
                await client.client.patchFixedCommentSet({ defaultCommentId: set.defaultCommentId }, set.setId, auditForm.Annotation);
            }

            setShowAuditModal(false);
            setSuccess(true);
            setChangedDefaultComments([]);
        } catch (e) {
            setErr(e.Message);
        } finally {
            setIsBusy(false);
        }
    };

    const onDefaultAcceptableCommentChanged = (setId, defaultAcceptableCommentId) => {
        let defaultChangedComments = changedDefaultComments.filter(x => x.setId != setId);
        let set = data.find(x=>x.id == setId);
        if (defaultAcceptableCommentId != DefaultFixedCommentOptionTypes.None) {
            defaultChangedComments.push({
                setId: setId,
                defaultCommentId: defaultAcceptableCommentId
            });
        }
        else if(set.defaultCommentId != null){
            defaultChangedComments.push({
                setId: setId,
                defaultCommentId: null
            });
        }
        setChangedDefaultComments(defaultChangedComments);
    }

    const handleClose = () => {
        setShowAuditModal(false);
    };

    if (sessionTypes.length === 0 || fixedCommentSetTypes.length === 0 || fixedCommentSetEntityTypes.length === 0)
        return <LoadingWithMessage message="Loading Fixed Comment Sets..." />

    return (initialDefaultComments ? <><Form onSubmit={handleSubmit} initialValues={initialDefaultComments as unknown as Record<string, unknown>}>
        {({ formState, formApi }) => (<>
            <Alert show={success} category={AlertCategoryEnum.Success}>Default Comments Updated Successfully</Alert>
            <Table automationId="commentListTable" data={data}>
                <Column field="studySet" header="Set Type" formatter={f => fixedCommentSetTypes.find(st => st.id === f.commentSetType).name} />
                <Column field="studySet" header="Session Type" formatter={f => sessionTypes.find(st => st.id === f.sessionType).name} />
                <Column field="entityType" header="Entity Type" formatter={f => fixedCommentSetEntityTypes.find(et => et.id === f).name} />
                <Column field={null} header="Default Acceptable Comment" formatter={f => <DefaultAcceptableCommentDropdown onChange={(setId, defaultAcceptableCommentId) => onDefaultAcceptableCommentChanged(setId, defaultAcceptableCommentId)} key={"acceptableComment" + f.id} value={changedDefaultComments.find(x => x.setId == f.id)?.defaultCommentId ?? f.defaultCommentId ?? DefaultFixedCommentOptionTypes.None} set={f} />} />
                <Column formatter={s => <FixedCommentSetHamburgerMenu set={s} />} />
            </Table>

            <button id="updateDefaultCommentsButton" disabled={isEqual(formState.values, initialDefaultComments) || !data?.length} onClick={(e) => {
                e.preventDefault();
                formApi.submitForm();
            }} className="btn btn-primary">Update Default Comments</button>
        </>)}

    </Form>
        {showAuditModal ? <AuditModal error={err} header="Update Default Fixed Comments" isSubmitting={isBusy} onClose={handleClose} onSubmit={(v) => handleAuditSubmit(v)} /> : null}
    </> : <Alert show={true} category={AlertCategoryEnum.Info}>There are no fixed comments configured currently</Alert>);
};


export default FixedCommentsTable;
